import { AnimationEvent, useTriggerAction } from '@libs/hooks';
import { selectInternalWidgets, useAppStore } from '@libs/store';
import { SystemEventTypeEnum, TBottomSheetProps } from '@libs/types';
import { ConditionalRender, eventBus, Portal } from '@libs/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { shallowEqual } from 'react-redux';
import { useIsOpenBS } from '../../hooks/useIsOpenBS';
import ClassicBottomSheet from './ClassicBottomSheet';
import FullScreenBottomSheet from './FullScreenBottomSheet';

const BottomSheet = ({ widgetId, modal, setBottomSheet, bottomSheets, WidgetsList }: TBottomSheetProps) => {
    const { setBottomSheetIsOpen } = useIsOpenBS();

    const [classicStack, setClassicStack] = useState<string[]>([]);
    const [fullScreenStack, setFullScreenStack] = useState<string[]>([]);
    const [animatedCloseId, setAnimatedCloseId] = useState<string | null>(null);

    const [prevWidgetId, setPrevWidgetId] = useState(widgetId);

    const internalIds = useMemo(() => new Set(bottomSheets ? Object.keys(bottomSheets) : []), [bottomSheets]);

    const store = useAppStore();
    const mutableInternalIdWidgetsRef = useRef(selectInternalWidgets(store.getState(), internalIds));

    useEffect(() => {
        mutableInternalIdWidgetsRef.current = selectInternalWidgets(store.getState(), internalIds);
        return store.subscribe(() => {
            const next = selectInternalWidgets(store.getState(), internalIds);
            if (shallowEqual(mutableInternalIdWidgetsRef.current, next)) return;
            mutableInternalIdWidgetsRef.current = next;
        });
    }, [store, internalIds]);

    const { triggerAction } = useTriggerAction();

    // Паттерн "обновление состояния во время рендера" для синхронизации с пропсами
    if (widgetId !== prevWidgetId) {
        setPrevWidgetId(widgetId);

        if (widgetId && bottomSheets?.[widgetId]) {
            const sheet = bottomSheets[widgetId];
            if (sheet.options?.fullScreen) {
                if (!fullScreenStack.includes(widgetId)) {
                    setFullScreenStack([...fullScreenStack, widgetId]);
                }
            } else {
                if (!classicStack.includes(widgetId)) {
                    setClassicStack([...classicStack, widgetId]);
                }
            }
        }
    }

    if (!widgetId && !animatedCloseId) {
        const topSheetId = classicStack.at(-1) ?? fullScreenStack.at(-1);
        if (topSheetId) {
            setAnimatedCloseId(topSheetId);
        }
    }

    const triggerAllActions = useCallback(
        (bsInternalId: string) => {
            const bsWidget = mutableInternalIdWidgetsRef.current[bsInternalId];
            if (bsWidget && Array.isArray(bsWidget.actions) && bsWidget.actions?.length > 0) {
                bsWidget.actions.forEach((action) => triggerAction(action, { widgetAction: bsWidget }));
            }
        },
        [triggerAction],
    );

    useEffect(() => {
        if (widgetId && bottomSheets?.[widgetId]?.isOpen) {
            setBottomSheetIsOpen(widgetId, false);
        }
        if (bottomSheets) {
            Object.keys(bottomSheets).forEach((internalId) => {
                eventBus.subscribeOnSpecificWidget(SystemEventTypeEnum.EXECUTE, internalId, () => {
                    triggerAllActions(internalId);
                });
            });
        }

        return () => {
            if (bottomSheets) {
                Object.keys(bottomSheets).forEach((internalId) => {
                    eventBus.unsubscribeSpecificWidget(SystemEventTypeEnum.EXECUTE, internalId);
                });
            }
        };
    }, [widgetId, bottomSheets, setBottomSheetIsOpen, triggerAllActions]);

    const currentClassicId = useMemo(() => classicStack.at(-1), [classicStack]);
    const currentFullScreenId = useMemo(() => fullScreenStack.at(-1), [fullScreenStack]);

    const currentClassicSheet = useMemo(() => (currentClassicId ? bottomSheets?.[currentClassicId] : undefined), [bottomSheets, currentClassicId]);
    const currentFullScreenSheet = useMemo(() => (currentFullScreenId ? bottomSheets?.[currentFullScreenId] : undefined), [bottomSheets, currentFullScreenId]);

    const cleanupStack = useCallback(
        (idToClear: string) => {
            flushSync(() => {
                const newClassicStack = classicStack.filter((id) => id !== idToClear);
                const newFullScreenStack = fullScreenStack.filter((id) => id !== idToClear);

                setClassicStack(newClassicStack);
                setFullScreenStack(newFullScreenStack);
                setAnimatedCloseId(null);

                const nextVisibleSheetId = newClassicStack.at(-1) ?? newFullScreenStack.at(-1);
                setBottomSheet(nextVisibleSheetId);
            });
        },
        [classicStack, fullScreenStack, setBottomSheet],
    );

    useEffect(() => {
        if (animatedCloseId) {
            const timer = setTimeout(() => {
                cleanupStack(animatedCloseId);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [animatedCloseId, cleanupStack]);

    const showClassic = !!(currentClassicId && currentClassicSheet && currentClassicSheet?.uid?.length > 0);
    const showFullScreen = !showClassic && !!(currentFullScreenId && currentFullScreenSheet && currentFullScreenSheet?.uid?.length > 0);

    const closeSheet = useCallback((idToClose: string) => {
        setAnimatedCloseId(idToClose);
    }, []);

    const handleAnimationEnd = useCallback(
        (e: AnimationEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) return;
            if (!animatedCloseId) return;

            cleanupStack(animatedCloseId);
        },
        [animatedCloseId, cleanupStack],
    );

    if (!showClassic && !showFullScreen) {
        return null;
    }

    return (
        <Portal>
            <ConditionalRender condition={!!currentFullScreenSheet}>
                {fullScreenStack.map((sheetId) => {
                    const sheet = bottomSheets?.[sheetId];
                    if (!sheet) {
                        return null;
                    }
                    return (
                        <FullScreenBottomSheet
                            key={sheetId}
                            widgetId={sheetId}
                            modal={modal}
                            isTop={sheetId === currentFullScreenId}
                            setAnimatedClose={() => closeSheet(sheetId)}
                            animatedClose={animatedCloseId === sheetId}
                            closeAnimationPopup={handleAnimationEnd}
                            bottomSheet={sheet}
                            WidgetsList={WidgetsList}
                        />
                    );
                })}
            </ConditionalRender>
            <ConditionalRender condition={!!currentClassicSheet}>
                <ClassicBottomSheet
                    widgetId={currentClassicId}
                    bottomSheet={currentClassicSheet}
                    modal={modal}
                    animatedClose={animatedCloseId === currentClassicId}
                    setAnimatedClose={() => closeSheet(currentClassicId ?? '')}
                    closeAnimationPopup={handleAnimationEnd}
                    WidgetsList={WidgetsList}
                />
            </ConditionalRender>
        </Portal>
    );
};
export default BottomSheet;
